import React from 'react';
import { Card } from '../ui';
import { TrendingUp, TrendingDown, Minus, MapPin, Clock, Fuel } from 'lucide-react';

const RouteStatsCard = ({ title, stats, icon: Icon, loading = false }) => {
  if (loading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4"></div>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card className="p-6">
        <p className="text-gray-500 text-sm">No data available</p>
      </Card>
    );
  }

  const getMetricIcon = (metric) => {
    switch (metric) {
      case 'distance':
        return MapPin;
      case 'duration':
        return Clock;
      case 'fuel':
        return Fuel;
      default:
        return Icon;
    }
  };

  const formatValue = (value, metric) => {
    if (value === null || value === undefined) return 'N/A';

    switch (metric) {
      case 'distance':
        return `${Math.round(value)} km`;
      case 'duration':
        return `${value.toFixed(1)} hrs`;
      case 'fuel':
        return `${value.toFixed(1)} L`;
      default:
        return value.toString();
    }
  };

  const getTrendIcon = (avg, current) => {
    if (!avg || !current) return Minus;
    if (current > avg * 1.1) return TrendingUp;
    if (current < avg * 0.9) return TrendingDown;
    return Minus;
  };

  const getTrendColor = (avg, current) => {
    if (!avg || !current) return 'text-gray-500';
    if (current > avg * 1.1) return 'text-red-500';
    if (current < avg * 0.9) return 'text-green-500';
    return 'text-gray-500';
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {Icon && <Icon className="w-5 h-5 text-blue-600" />}
      </div>

      <div className="space-y-4">
        {Object.entries(stats).map(([metric, values]) => {
          const MetricIcon = getMetricIcon(metric);
          const TrendIcon = getTrendIcon(values.avg, values.current);
          const trendColor = getTrendColor(values.avg, values.current);

          return (
            <div key={metric} className="border-b border-gray-100 pb-3 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <MetricIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500 capitalize">
                    {metric}
                  </span>
                </div>
                <TrendIcon className={`w-4 h-4 ${trendColor}`} />
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-gray-500">Avg</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(values.avg, metric)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Min</p>
                  <p className="text-sm font-semibold text-green-600">
                    {formatValue(values.min, metric)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Max</p>
                  <p className="text-sm font-semibold text-red-600">
                    {formatValue(values.max, metric)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {stats.total_journeys !== undefined && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Based on <span className="font-semibold text-gray-900">{stats.total_journeys}</span> journeys
          </p>
        </div>
      )}
    </Card>
  );
};

export default RouteStatsCard;
